/**
 * The Update panel's private note with @mentions, on #113's picker logic (`../composer/mentions`): the textarea shows
 * "@Name"; on save every picked "@Name" still in the text becomes Desk's own mention markup (`applyMentions`), so Desk
 * notifies the person natively and the API rings their Patchbay bell (post_staff_note checks and notifies).
 */
import { Textarea } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import { activeMention, filterPeople, insertMention, mentionPeopleQuery, type PickedMention } from '../composer/mentions'

export function NoteField({
  value,
  picked,
  onChange,
  placeholder,
  disabled,
}: {
  value: string
  picked: PickedMention[]
  onChange: (text: string, picked: PickedMention[]) => void
  placeholder?: string
  disabled?: boolean
}) {
  const ref = React.useRef<HTMLTextAreaElement>(null)
  const [mention, setMention] = React.useState<{ start: number; query: string } | null>(null)
  const people = useQuery({ ...mentionPeopleQuery(), enabled: mention !== null })
  const choices = mention && people.data ? filterPeople(people.data.people, mention.query, 6) : []

  const pick = (p: PickedMention) => {
    if (!mention) return
    const caret = ref.current?.selectionStart ?? value.length
    const out = insertMention(value, mention.start, caret, p.name)
    setMention(null)
    onChange(out.text, [...picked.filter((x) => x.zuid !== p.zuid), p])
    requestAnimationFrame(() => {
      ref.current?.focus()
      ref.current?.setSelectionRange(out.caret, out.caret)
    })
  }

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        aria-label="Private note"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        className="min-h-20"
        onChange={(e) => {
          const text = e.currentTarget.value
          setMention(activeMention(text, e.currentTarget.selectionStart ?? text.length))
          onChange(text, picked)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && mention !== null) {
            e.stopPropagation()
            setMention(null)
          }
        }}
      />
      {mention !== null ? (
        <fieldset
          aria-label="Mention someone"
          className="absolute z-10 mt-1 w-64 rounded-md border border-border bg-popover p-1 text-sm shadow-md"
        >
          {people.isError ? <p className="px-2 py-1.5 text-destructive">Staff list unreachable; write the note without a mention</p> : null}
          {people.isPending ? <p className="px-2 py-1.5 text-muted-foreground">Loading staff…</p> : null}
          {people.data && choices.length === 0 ? (
            <p className="px-2 py-1.5 text-muted-foreground">Nobody matches “{mention.query}”</p>
          ) : null}
          {choices.map((p) => (
            <button
              key={p.zuid}
              type="button"
              className="block w-full cursor-pointer rounded px-2 py-1.5 text-left hover:bg-muted"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick({ zuid: p.zuid, name: p.name, token: p.token })}
            >
              @{p.name}
            </button>
          ))}
        </fieldset>
      ) : null}
    </div>
  )
}
